import React, { useContext, useEffect, useRef, useState } from 'react';
import { Container, Table, Button, Row, Col, Input } from 'reactstrap';
import PaginationMaterial from "../../components/PaginationMaterial/PaginationMaterial";
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import Header from '../../components/Headers/Header';
import AdminNavbar from '../../components/Navbars/AdminNavbar/AdminNavbar';
import { AuthContext } from '../../context/AuthContext';
import { InstituicaoContext } from '../../context/InstituicaoContext';
import { InstituicaoType, EditInstituicaoType } from '../../context/hooks/useInstituicao';
import { ResponsavelContext } from '../../context/ResponsavelContext';
import useUser from '../../context/hooks/useUser';
import { createPermissionChecker, PermissionModule } from '../../types/permissions';

import { CreateInstituicaoModal } from '../../components/Instituicao/CreateInstituicaoModal';
import { EditInstituicaoModal } from '../../components/Instituicao/EditInstituicaoModal';

const Instituicao = () => {
    const { objUserTk } = useContext(AuthContext);
    const { getInstituicaoPaged, deleteInstituicao, listInstituicao, pageCount } = useContext(InstituicaoContext);
    const { getResponsavelPaged } = useContext(ResponsavelContext);

    const { getPermissionsByUser, userPermissions, useLegacyPermissions, modulePermissions } = useUser();
    const hasPermission = createPermissionChecker(useLegacyPermissions, userPermissions, modulePermissions);

    const [loaging, setLoading] = useState(true);

    const [editingInstituicao, setEditingInstituicao] = useState<EditInstituicaoType | null>(null);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);

    const [dataSearch, setDataSearch] = useState({ name: '' });

    // evita race condition (resultado antigo chegando depois do novo)
    const lastRequestIdRef = useRef(0);

    useEffect(() => {
        setLoading(true);

        // carrega as duas listas iniciais
        getResponsavelPaged(1, "", objUserTk?.EmpresaId, 0, 9);

        const requestId = ++lastRequestIdRef.current;
        getInstituicaoPaged(1, "", objUserTk?.EmpresaId, 9)
            .then(() => {
                if (requestId === lastRequestIdRef.current) setLoading(false);
            })
            .catch(error => {
                console.error("Erro ao buscar instituição:", error);
                if (requestId === lastRequestIdRef.current) setLoading(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const fetchPermissions = async () => {
            if (objUserTk?.UserId) {
                await getPermissionsByUser(objUserTk.UserId);
            }
        };
        fetchPermissions().catch(error => console.error("Erro ao carregar permissões:", error));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [objUserTk?.UserId]);

    // ✅ AUTO-FILTRO enquanto digita (com debounce)
    useEffect(() => {
        const empresaId = objUserTk?.EmpresaId;
        if (!empresaId) return;

        const term = (dataSearch.name ?? '').trim();

        const timeout = setTimeout(() => {
            setLoading(true);
            const requestId = ++lastRequestIdRef.current;

            getInstituicaoPaged(1, term, empresaId, 9)
                .then(() => {
                    if (requestId === lastRequestIdRef.current) setLoading(false);
                })
                .catch(() => {
                    if (requestId === lastRequestIdRef.current) setLoading(false);
                });
        }, 400); // ajuste aqui: 300-500ms costuma ser ótimo

        return () => clearTimeout(timeout);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataSearch.name, objUserTk?.EmpresaId]);

    const onChangePaginationCommon = async (e: any, value: any) => {
        await getInstituicaoPaged(value, (dataSearch.name ?? '').trim(), objUserTk.EmpresaId, 9);
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setDataSearch({ ...dataSearch, name: e.target.value });
    };

    const handleClearFilters = () => {
        setDataSearch({ name: '' });
        setLoading(true);

        const requestId = ++lastRequestIdRef.current;
        getInstituicaoPaged(1, "", objUserTk?.EmpresaId, 9)
            .then(() => {
                if (requestId === lastRequestIdRef.current) setLoading(false);
            })
            .catch(() => {
                if (requestId === lastRequestIdRef.current) setLoading(false);
            });
    };

    const handleEditInstituicao = async (instituicao: InstituicaoType) => {
        const updateData: EditInstituicaoType = {
            id: instituicao.id,
            name: instituicao.name,
            email: instituicao.email,
            CEP: instituicao.cep,
            state: instituicao.state,
            city: instituicao.city,
            neighborhood: instituicao.neighborhood,
            address: instituicao.address,
            number: instituicao.number,
            directorPhone: instituicao.directorPhone,
            isPrivate: instituicao.isPrivate,
            pix: instituicao.pix,
            password: "",
        };

        setEditingInstituicao(updateData);
        await getResponsavelPaged(1, "", objUserTk?.EmpresaId, instituicao.id, 9);
        setEditModalOpen(true);
    };

    const handleDeleteInstituicao = (instituicaoId: number) => {
        if (window.confirm("Tem certeza que deseja excluir esta instituição?")) {
            deleteInstituicao(instituicaoId)
                .then(() => {
                    setTimeout(() => {
                        getInstituicaoPaged(1, (dataSearch.name ?? '').trim(), objUserTk?.EmpresaId, 9);
                    }, 300);
                })
                .catch(error => {
                    console.error("Erro ao excluir instituição:", error);
                });
        }
    };

    if (loaging) {
        return <LoadingSpinner />;
    }

    return (
        <>
            <Header />
            <AdminNavbar
                brandText={"Gerenciar Instituições"}
                search={false}
                titleSearch={""}
            />

            <Container className="mt-5">
                <Row className="align-items-center mb-4">
                    <Col xs="12" md="10" lg="10" className="mb-2">
                        <Input
                            className="form-control-alternative"
                            type="text"
                            name="search"
                            id="search"
                            placeholder="Buscar por nome"
                            value={dataSearch.name}
                            onChange={handleSearchChange}
                        />
                    </Col>

                    <Col xs="12" md="2" lg="2" className="d-flex justify-content-start justify-content-md-end mb-2">
                        <Button
                            style={{ color: "#FFF", backgroundColor: "#d9534f" }}
                            type="button"
                            size="m"
                            onClick={handleClearFilters}
                        >
                            Limpar
                        </Button>
                    </Col>

                    {hasPermission(PermissionModule.InstituicoesCursos, 1) && (
                        <Col xs="12" className="text-right mt-3">
                            <Button
                                style={{ color: "#FFF", backgroundColor: "#585858" }}
                                size="m"
                                onClick={() => { setCreateModalOpen(true); }}
                            >
                                Adicionar instituição
                            </Button>
                        </Col>
                    )}
                </Row>

                {hasPermission(PermissionModule.InstituicoesCursos, 2) ? (
                    <Row className="mt-4">
                        <Col>
                            <Table hover responsive className="table-striped">
                                <thead>
                                    <tr>
                                        <th>Sequência</th>
                                        <th>Nome</th>
                                        <th>Email</th>
                                        <th>Editar</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {listInstituicao.map((instituicao: InstituicaoType) => (
                                        <tr key={instituicao.id}>
                                            <td>{instituicao.id}</td>
                                            <td>{instituicao.name}</td>
                                            <td>{instituicao.email}</td>
                                            <td>
                                                {hasPermission(PermissionModule.InstituicoesCursos, 3) && (
                                                    <Button onClick={() => handleEditInstituicao(instituicao)}>
                                                        <i className="fa-solid fa-pen-to-square"></i>
                                                    </Button>
                                                )}
                                                {hasPermission(PermissionModule.InstituicoesCursos, 4) && (
                                                    <Button onClick={() => handleDeleteInstituicao(instituicao.id)} color="danger">
                                                        <i className="fa-solid fa-trash"></i>
                                                    </Button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>

                            <div className="d-flex justify-content-end mt-3">
                                {pageCount ? (
                                    <div className="d-flex justify-content-center mt-4">
                                        <PaginationMaterial
                                            onChange={(e: any, value: any) => onChangePaginationCommon(e, value)}
                                            count={pageCount}
                                        />
                                    </div>
                                ) : null}

                                {listInstituicao.length === 0 && (
                                    <div style={{ color: "red" }}>NÃO FOI ENCONTRADO NENHUM REGISTRO</div>
                                )}
                            </div>
                        </Col>
                    </Row>
                ) : (
                    <Row className="mt-4">
                        <Col>
                            <div style={{ color: "red", textAlign: "center" }}>
                                Você não tem permissão para visualizar as instituições.
                            </div>
                        </Col>
                    </Row>
                )}
            </Container>

            <CreateInstituicaoModal
                isOpen={createModalOpen}
                toggle={() => setCreateModalOpen(false)}
                onInstituicaoCriada={() => getInstituicaoPaged(1, "", objUserTk?.EmpresaId, 9)}
            />

            {editingInstituicao && (
                <EditInstituicaoModal
                    isOpen={editModalOpen}
                    toggle={() => setEditModalOpen(false)}
                    instituicaoData={editingInstituicao}
                    reloadList={() => getInstituicaoPaged(1, "", objUserTk?.EmpresaId, 9)}
                />
            )}
        </>
    );
};

export default Instituicao;